/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-01-05 18:56:16
 */

import React from 'react'
import { Tag } from 'antd'
import type { ColumnType } from 'antd/es/table'
import IconPlay from '../../../images/icon_play.png'

interface Department {
  id: string
  name: string
}

interface Owner {
  userDepartments: Department[]
}

interface DataItem {
  owners: Owner[]
  [key: string]: any
}

export const createDepartmentsColumns = (
  dataSource: DataItem[]
): ColumnType<any>[] => {
  if (!dataSource || dataSource.length === 0) return []
  const departmentsLevel = Math.max(
    ...dataSource.map((item) => {
      return Math.max(...item.owners.map((o) => o.userDepartments.length))
    })
  )

  const columns: ColumnType<any>[] = []
  const title = [
    '一级部门',
    '二级部门',
    '三级部门',
    '四级部门',
    '五级部门',
    '六级部门',
    '七级部门',
    '八级部门',
    '九级部门',
    '十级部门',
    '十一级部门',
    '十二级部门',
    '十三级部门',
    '十四级部门',
    '十五级部门',
    '十六级部门',
    '十七级部门',
    '十八级部门',
    '十九级部门',
    '二十级部门'
  ]
  for (let i = 0; i < departmentsLevel; i++) {
    columns.push({
      title: title[i],
      dataIndex: `department${i}`,
      key: `department${i}`,
      width: 100,
      render: (_: any, record: DataItem) => {
        const curLevelDepartments: Department[] = record.owners
          .map((o) => (o.userDepartments ? o.userDepartments[i] : null))
          .reduce((acc: Department[], cur) => {
            if (cur && acc.findIndex((a) => a.id === cur.id) === -1) {
              acc.push(cur)
            }
            return acc
          }, [])
        if (curLevelDepartments.length === 0) return null
        if (curLevelDepartments.length === 1)
          return <div>{curLevelDepartments[0].name}</div>
        return (
          <>
            {curLevelDepartments.map((d, index) => (
              <div key={index}>
                {d.name}
                {index < curLevelDepartments.length - 1 ? '、' : ''}
              </div>
            ))}
          </>
        )
      }
    })
  }
  return columns
}

export const shelfApprovalColumns = [
  {
    title: '审批结果',
    dataIndex: ['shelfVerification', 'approvalStatus'],
    key: 'shelfVerification.approvalStatus',
    width: 126,
    render: (text, record) => {
      if (record.shelfVerification?.exist) {
        const result = shelfApprovalResult.find((item) => item.value === text)
        if (result) {
          return (
            <Tag color={result.tagColor} style={{ color: result.tagTextColor }}>
              {result.label}
            </Tag>
          )
        }
      }
      return '-'
    }
  },
  {
    title: '审批人',
    dataIndex: ['shelfVerification', 'approvalUser'],
    key: 'shelfVerification.approvalUser',
    width: 126,
    render: (_text, record) => {
      if (record.shelfVerification?.exist) {
        return record.shelfVerification.approvalUser?.nickname || '-'
      }
      return '-'
    }
  },
  {
    title: '审批意见',
    dataIndex: ['shelfVerification', 'approvalOpinion'],
    key: 'shelfVerification.approvalOpinion',
    width: 400,
    render: (text, record) => {
      if (record.shelfVerification?.exist) {
        return text || '-'
      }
      return '-'
    }
  }
]

export const shelfApprovalResult = [
  { label: '已通过', value: 1, tagColor: '#B7EDB1', tagTextColor: '#237B19' },
  { label: '未通过', value: 2, tagColor: '#FBBFBC', tagTextColor: '#D83931' },
  { label: '审批中', value: 0, tagColor: '#DEE8FF', tagTextColor: '#245BDB' }
]

export const parseFetchParams = (filter) => {
  const userIDList = (filter.departmentUsers || []).map((user) => user.userID)

  // 陈列核销审批水印
  const watermarks = (filter.watermarks || []).map((watermark) => {
    const [watermarkBaseID, groupWatermarkID] = watermark.split('+')
    return { watermarkBaseID, groupWatermarkID }
  })

  const approvalUserIDList = (filter.approvalUsers || []).map(
    (user) => user.userID
  )

  const approvalDepartmentIDList = (filter.approvalDepartments || []).map(
    (department) => Number(department.id)
  )

  return {
    userIDList,
    watermarks,
    approvalUserIDList,
    approvalDepartmentIDList
  }
}

export const getMediaType = (url) => {
  const [s] = url.split('?')

  return s.endsWith('.mp4') ? 'video' : 'image'
}

export const getImageUrl = (url) => {
  if (getMediaType(url) === 'video') {
    const [s] = url.split('?')

    // return `${s}?x-oss-process=video/snapshot,t_0,m_fast,ar_auto,h_640,w_640`
    return `${s}?x-oss-process=video/snapshot,t_0,m_fast,ar_auto`
  }

  return url
}

export const renderVisitPhotos = ({ urls, onPreview }) => {
  const photos = (urls || []).map((url) => {
    const mediaType = getMediaType(url)
    const mediaUrl = getImageUrl(url)

    if (mediaType === 'video') {
      return {
        mediaType: 1,
        videoURL: url,
        smallURL: mediaUrl,
        largeURL: mediaUrl
      }
    }

    return {
      mediaType: 0,
      smallURL: mediaUrl,
      largeURL: mediaUrl
    }
  })

  return (
    <div className="photoList">
      {photos.map(({ mediaType, smallURL }, index) => {
        return (
          <div
            className="photo"
            key={index}
            onClick={() => {
              onPreview(photos, index)
            }}
          >
            <img className="small-img" src={smallURL} alt="" />

            {mediaType === 1 && (
              <img className="play-icon" src={IconPlay} alt="icon_play" />
            )}
          </div>
        )
      })}
    </div>
  )
}

export const createVisitScreenColumns = ({ photoScreenColumns, onPreview }) => {
  // name: 'xx照片', '其他拜访照片'
  // photoScreen: 'xx', ''
  return photoScreenColumns.map((name, index) => {
    return {
      title: name,
      dataIndex: `visitPhotos_${name}`,
      key: `visitPhotos_${name}`,
      width: 180,
      render: (text, record) => {
        const photos =
          record.visitPhotos
            ?.filter((photo) => {
              if (name === '其他拜访照片') {
                return photo.photoScreen === ''
              }
              return photo.photoScreen + '照片' === name
            })
            .map((photo) => photo.photoUrl) || []
        return renderVisitPhotos({
          urls: photos,
          onPreview
        })
      }
    }
  })
}

/**
 * 补货信息列
 * @param purchaseColumns
 * @returns {*}
 */
export const createPurchaseColumn = (): ColumnType<any> => {
  return {
    title: '补货信息',
    dataIndex: 'purchase',
    key: 'purchase',
    width: 200,
    render: (_, record) => {
      const { purchase } = record
      if (!purchase || purchase.length === 0) return '未补货'
      return (
        <div>
          {purchase?.map((item, index) => {
            const { goodsID, saleCnt, goodsName } = item
            return (
              <div key={index}>
                {goodsName} x{saleCnt}
              </div>
            )
          })}
        </div>
      )
    }
  }
}
